import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Table, Button, Input, Card, Alert, Loader, Badge, ProgressBar, FileUpload, Dialog } from '@dreamtree-org/twreact-ui';
import { Eye, RotateCcw, Trash2 } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { runUpload, errText } from '@/lib/videoUpload';
import Can from '@rbac/Can';
import { useAuth, useCan } from '@rbac/useCan';

const STATUS_VARIANT = {
  uploading: 'warning',
  processing: 'info',
  ready: 'success',
  error: 'error',
};

const VISIBILITY_VARIANT = {
  private: 'default',
  passcode: 'success',
  unlisted: 'info',
};

const ACCEPT = 'video/mp4,video/webm,video/quicktime';

// Mirrors VIDEO_MAX_UPLOAD_MB on the server. This copy is UX — it tells the user
// immediately instead of after a long upload — and NOT a control: the real limit is
// enforced server-side against the byte count Spaces reports (Video.onCompleteAction).
const MAX_MB = 10240;

const prettySize = (bytes) => {
  if (!bytes) return '—';
  const mb = bytes / 1048576;
  if (mb >= 1024) return `${(mb / 1024).toFixed(1)} GB`;
  // Sub-megabyte files round to a bare "0 MB", which reads as a failed upload rather than
  // a small one. Fall back to KB below 1MB.
  if (mb < 1) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${Math.round(mb)} MB`;
};

// Module-level: nothing here closes over component state. The row-action column is added
// in the component, since it needs handlers.
const COLUMNS = [
  {
    key: 'title',
    label: 'Title',
    render: (row) => (
      <Link className="text-primary-600 font-medium hover:underline" to={`/videos/${row.id}`}>
        {row.title}
      </Link>
    ),
  },
  {
    key: 'status',
    label: 'Status',
    render: (row) => (
      <span>
        <Badge variant={STATUS_VARIANT[row.status] || 'default'}>{row.status}</Badge>
        {/* The reason an upload failed, where the failure happened. Without it a dead row
            is indistinguishable from one still in flight. */}
        {row.error ? <span className="block text-xs opacity-70 mt-1">{row.error}</span> : null}
      </span>
    ),
  },
  {
    key: 'visibility',
    label: 'Visibility',
    render: (row) => (
      <Badge variant={VISIBILITY_VARIANT[row.visibility] || 'default'}>
        {/* Flag a passcode video with no passcode saved — it is unwatchable by anyone. */}
        {row.visibility === 'passcode' && !row.has_passcode ? 'passcode (not set)' : row.visibility}
      </Badge>
    ),
  },
  { key: 'size_bytes', label: 'Size', render: (row) => prettySize(row.size_bytes) },
  { key: 'created_at', label: 'Uploaded' },
];

/**
 * Everything about running an upload: progress, the tab-close guard, and the one call the
 * form and the per-row Retry both go through.
 *
 * Shared rather than duplicated because a retry IS an upload — the only difference is that
 * the row already exists. `onSettled` reloads either way: on failure the row now carries
 * its reason, and not refreshing was why a failed upload looked like nothing had happened.
 */
function useUploader({ fetchApi, onSettled }) {
  const [progress, setProgress] = useState(null);
  const [stage, setStage] = useState('');
  const [err, setErr] = useState('');
  const busy = progress !== null;

  // Closing the tab mid-PUT abandons the row in `uploading`. The prose warning on the form
  // was the only thing standing between a user and that, and prose loses.
  useEffect(() => {
    if (!busy) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [busy]);

  const send = async ({ file, title, existingId = null }) => {
    setErr('');
    setProgress(0);
    let ok = false;
    try {
      await runUpload({
        fetchApi, file, title, existingId, onStage: setStage, onProgress: (f) => setProgress(Math.round(f * 100)),
      });
      ok = true;
    } catch (ex) {
      setErr(errText(ex));
    } finally {
      setProgress(null);
      setStage('');
      await onSettled();
    }
    return ok;
  };

  return { send, busy, progress, stage, err, setErr };
}

/** The upload form. Owns only the file and title it is composing. */
function UploadForm({ uploader }) {
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const { busy, progress, stage } = uploader;

  const pickFile = (files) => {
    const f = (files && files[0]) || null;
    setFile(f);
    // Default the title to the filename minus its extension — almost always what the user
    // wanted, and still editable.
    if (f && !title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return;
    if (await uploader.send({ file, title })) {
      setFile(null);
      setTitle('');
    }
  };

  return (
    <Card className="p-4">
      <form onSubmit={submit} className="space-y-4">
        <FileUpload
          accept={ACCEPT}
          multiple={false}
          maxSize={MAX_MB * 1024 * 1024}
          files={file ? [file] : []}
          onFileSelect={pickFile}
          onFileRemove={() => setFile(null)}
          disabled={busy}
        />
        <Input
          label="Title"
          placeholder="Give the video a name"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={busy}
          required
        />
        {busy ? (
          <div className="space-y-1">
            <ProgressBar value={progress} max={100} showValue striped animated />
            <p className="text-sm opacity-70">{stage}</p>
          </div>
        ) : null}
        <p className="text-sm opacity-70">
          MP4, WebM or MOV, up to {MAX_MB / 1024} GB. The file uploads straight to storage —
          leave this tab open until it finishes. If it is interrupted, use Retry on the row
          rather than uploading again.
        </p>
        <Button type="submit" loading={busy} disabled={!file || !title.trim()}>Upload video</Button>
      </form>
    </Card>
  );
}

export default function Videos() {
  const { fetchApi } = useAppApi();
  const navigate = useNavigate();
  const user = useAuth();
  const can = useCan();
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  // Kept apart from the uploader's error: a failed LIST used to render under the heading
  // "Upload failed", which sent people looking at the wrong half of the page.
  const [listErr, setListErr] = useState('');

  const [confirmDelete, setConfirmDelete] = useState(null);
  // The row a retry is being picked for. The file comes from one hidden input, so the retry
  // doesn't need a FileUpload widget on every row.
  const [retryFor, setRetryFor] = useState(null);
  const retryInput = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await fetchApi('crud', {
        model: 'Video', action: 'list', limit: 100, orderBy: { column: 'id', direction: 'desc' },
      });
      setVideos(Array.isArray(res) ? res : (res && res.data) || []);
      setListErr('');
    } catch (ex) {
      setListErr(errText(ex));
    } finally {
      setLoading(false);
    }
  }, [fetchApi]);

  useEffect(() => { load(); }, [load]);

  const uploader = useUploader({ fetchApi, onSettled: load });
  const { busy, setErr } = uploader;

  const startRetry = useCallback((row) => {
    setRetryFor(row);
    retryInput.current.value = '';
    retryInput.current.click();
  }, []);

  const onRetryFile = (e) => {
    const file = e.target.files && e.target.files[0];
    const row = retryFor;
    setRetryFor(null);
    if (file && row) uploader.send({ file, existingId: row.id });
  };

  const doDelete = async () => {
    const row = confirmDelete;
    setConfirmDelete(null);
    setErr('');
    try {
      await fetchApi('crud', { model: 'Video', action: 'delete', where: { id: row.id } });
      await load();
    } catch (ex) {
      setErr(errText(ex));
    }
  };

  // `replaceDefaultActions` is not optional: supplied actions otherwise MERGE onto the
  // library's built-in Edit/Delete/View — which would both restore console.log stubs and
  // reorder these into the defaults' order. It is also what keeps View/Retry/Delete in
  // this order.
  // Keyed on `user`, not `can`: useCan() returns a fresh closure every render, so listing it
  // would defeat the memo the library relies on for its per-row action cache.
  const actions = useMemo(() => [
    {
      // Navigation, not a Link: an inline action's content renders INSIDE the library's
      // <button>, where a nested <a> is invalid markup and double-activates.
      name: 'view',
      label: 'View',
      icon: <Eye size={16} />,
      inline: true,
      onClick: ({ row }) => navigate(`/videos/${row.id}`),
    },
    {
      // Only for a row that never published. The server re-mints a presigned PUT for
      // exactly those states, so a retry can never overwrite a finished video.
      name: 'retry',
      label: 'Retry',
      icon: <RotateCcw size={16} />,
      inline: true,
      hidden: (row) => !can('video', 'create')
        || (row.status !== 'uploading' && row.status !== 'error'),
      disabled: () => busy,
      onClick: ({ row }) => startRetry(row),
    },
    {
      name: 'delete',
      label: 'Delete',
      icon: <Trash2 size={16} />,
      inline: true,
      hidden: () => !can('video', 'delete'),
      onClick: ({ row }) => setConfirmDelete(row),
    },
  ], [user, busy, navigate, startRetry]);

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">Videos</h1>

      <Can module="video" action="create">
        <UploadForm uploader={uploader} />
      </Can>

      {uploader.err ? <Alert variant="error" title="Upload failed">{uploader.err}</Alert> : null}
      {listErr ? <Alert variant="error" title="Could not load videos">{listErr}</Alert> : null}

      {/* Retry re-uses the row, so it only ever needs the bytes. */}
      <input ref={retryInput} type="file" accept={ACCEPT} className="hidden" onChange={onRetryFile} />

      {loading
        ? <Loader text="Loading videos…" />
        : (
          <Table
            data={videos}
            columns={COLUMNS}
            pagination
            globalSearch
            actions={actions}
            replaceDefaultActions
          />
        )}

      <Dialog isOpen={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} title="Delete this video?">
        <div className="space-y-4">
          <p>
            “{confirmDelete && confirmDelete.title}” and its stored file will be removed
            permanently. Any page embedding it will stop working.
          </p>
          <div className="flex gap-2 justify-end">
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button variant="danger" onClick={doDelete}>Delete</Button>
          </div>
        </div>
      </Dialog>
    </div>
  );
}
